<script setup lang="ts">
import { BaseFile } from "~/classes/file/BaseFile"
import { PropertySize } from "~/classes/generated/interface"

defineProps({
  onlyIn: { type: Boolean }
})
const emit = defineEmits(["remove"])
const { state: instantState, matchingTags } = useInstantMenu()
const tagInput = defineModel<string>("tag")
const id = useId()
const items: Ref<string[]> = ref([])

instantState.value.tagSelectionMenusOpen[id] = false

const quitAndRemove = () => {
  delete instantState.value.tagSelectionMenusOpen[id]
  emit("remove")
}

const quit = () => {
  delete instantState.value.tagSelectionMenusOpen[id]
}

const selectAndClose = (tag: string) => {
  tagInput.value = tag
  delete instantState.value.tagSelectionMenusOpen[id]
}

watch(() => tagInput.value, async (tagInput?: string) => {
  if (tagInput)
    items.value = await matchingTags(tagInput)
  else
    items.value = []
})

/** If the user clicks out of the dropdown */
watch(() => instantState.value.tagSelectionMenusOpen[id], (active?: boolean) => {
  if (active)
    return
  // if there's no tag input value
  if (!tagInput.value)
    return quitAndRemove()
  // if the tag isn't valid
  if (BaseFile.isTagValid(tagInput.value) === false)
    return quitAndRemove()
  // If we have multiple of this tag in our facets, remove this
  const tagFacets = instantState
    .value
    .facets
    .filter(f => "hasTag" in f.facetType && f.facetType.hasTag === tagInput.value)
  if (tagFacets.length > 1)
    return quitAndRemove()
})
</script>

<template>
  <dropdown
    v-model:active="instantState.tagSelectionMenusOpen[id]"
    anchor-x="left"
    anchor-y="bottom"
    flow-x="right"
    flow-y="down"
    :open-on-mount="true"
  >
    <template #default="{ open }">
      <div class="tag-picker-container">
        <div class="tag-picker-description">
          Has Tag
        </div>
        <div class="tag-picker-outer" @click="open()">
          <file-property-tag
            v-if="tagInput"
            :value="tagInput"
            :size="PropertySize.Small"
          />
        </div>
        <div class="tag-picker-close" @click="quitAndRemove()">
          <icon-action type="cross" size="2xs" />
        </div>
      </div>
    </template>
    <template #menu>
      <nav-instant-facet-select
        v-model:query="tagInput"
        :items
        placeholder="Search for a tag..."
        class="tag-picker-select-container"
        @select="item => selectAndClose(item)"
        @cancel="quit()"
      >
        <template #option="{ item, selected }">
          <file-property-tag
            :value="item"
            :size="PropertySize.Medium"
          />
        </template>
      </nav-instant-facet-select>
    </template>
  </dropdown>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.tag-picker-container
  @apply flex flex-none items-center gap-[#{$menu-search-gap-item-facet}] relative
  @apply h-[24px]
  @apply rounded-[#{$menu-br-item}] overflow-hidden
  @apply t-menu-sm

.tag-picker-description
  @apply flex flex-none items-center
  @apply h-[24px] px-1.5
  @apply bg-$global-steel-accent
  @apply text-$global-t-secondary
  @apply cursor-pointer
  &:hover
    @apply bg-$global-steel-secondary

.tag-picker-close
  @apply flex flex-none items-center
  @apply h-full px-1
  @apply bg-$global-steel-accent
  @apply text-$global-steel-primary
  @apply cursor-pointer
  &:hover
    @apply bg-$global-steel-secondary

.tag-picker-outer
  @apply flex-none flex items-center gap-[#{$menu-search-gap-item-facet-section}]
  @apply h-full px-1
  @apply bg-$global-steel-accent
  @apply cursor-pointer
  &:hover
    @apply bg-$global-steel-secondary

.tag-picker-file-icon
  @apply text-$global-steel-primary

.tag-picker-selection
  @apply w-full

.tag-picker-select-container
  @apply mt-2

.tag-picker-option-wrapper
  &.selected, &:hover
    @apply bg-[#{$menu-bg-item-hover}] -m-1 p-1 rounded-[#{$menu-br-item}]

.inline-select-dropdown
  @apply flex flex-col gap-[#{$menu-gap-items}] px-[#{$menu-px-default}] py-[#{$menu-py-default}]
  @apply overflow-auto
  max-height: calc(100vh - 60px)

.inline-select-option
  @apply text-[#{$global-t-default}]
  @apply flex cursor-pointer items-center h-5 -m-1 p-1 gap-2
  @apply rounded-[#{$menu-br-item}]
  .icon
    @apply ml-auto h-4 w-4 text-[#{$global-bg-icon-secondary}]
  &.disabled
    @apply cursor-not-allowed opacity-50
  &:not(.disabled):hover
    @apply bg-[#{$menu-bg-item-hover}]
</style>
